import { useCallback, type CSSProperties, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner, SpinnerSize, MessageBar, MessageBarType, Icon } from '@fluentui/react'
import { apiService } from '../../services/apiService'
import { useApi } from '../../hooks/useApi'
import { useDashboardTokens, type DashboardTokens } from '../../theme/dashboardTokens'
import type {
  PostureData,
  SyncHealth,
  ApplicationChangesData,
  ApplicationChangeDataPoint,
} from '../../types'

/**
 * Lifecycle Command Center — KPI band (v2, Lane B / V7).
 *
 * The top "at a glance" strip. The 2-lens review split it into two labelled
 * zones to stop the naïve version double-counting the chart/rings and mixing
 * flow with state:
 *   - **Estate (now)** — point-in-time state from `/api/reports/posture` (V3):
 *     Managed · Expiring ≤30d · Unowned.
 *   - **Activity (window)** — what changed over the selected range from
 *     `/api/reports/changes`: Added · Removed · Net (added − removed).
 *
 * Deltas: a single "vs prior window" baseline (not per-tile), computed client-
 * side by fetching 2× the window and splitting. Raw Added/Removed are neutral
 * (adding apps isn't inherently good/bad); Net and the Estate attention states
 * (expiring/unowned > 0) carry health-direction colour. A compact sync-health
 * chip (V4) rounds it out — amber past the stale threshold.
 *
 * Range is the dashboard's shared 7/30/90 preset, passed in as `days` (the page
 * owns it and feeds both this band and the chart). Deeper/custom ranges live on
 * the Reports page, not here.
 */

interface KpiBandProps {
  /** Shared dashboard range (7 | 30 | 90). Drives the Activity zone + baseline. */
  days: number
}

const sumBy = (rows: ApplicationChangeDataPoint[], key: 'added' | 'removed') =>
  rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0)

export function KpiBand({ days }: KpiBandProps) {
  const navigate = useNavigate()
  const tokens = useDashboardTokens()

  const fetchPosture = useCallback(() => apiService.getPosture(), [])
  // Fetch 2× the window so we can split into current vs prior for the baseline.
  const fetchChanges = useCallback(() => apiService.getApplicationChanges(days * 2), [days])
  const fetchSync = useCallback(() => apiService.getSyncHealth(), [])

  const posture = useApi<PostureData>(fetchPosture)
  const changes = useApi<ApplicationChangesData>(fetchChanges, [days])
  const sync = useApi<SyncHealth>(fetchSync)

  const loading = posture.loading || changes.loading
  const error = posture.error || changes.error

  const pts = changes.data?.dataPoints ?? []
  const cur = pts.slice(-days)
  const prior = pts.slice(-days * 2, -days)
  const hasPrior = prior.length > 0

  const addedCur = sumBy(cur, 'added')
  const addedPrior = sumBy(prior, 'added')
  const removedCur = sumBy(cur, 'removed')
  const removedPrior = sumBy(prior, 'removed')
  const netCur = addedCur - removedCur
  const netPrior = addedPrior - removedPrior

  return (
    <section
      aria-label="Key metrics"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: 20,
        rowGap: 16,
      }}
    >
      {loading ? (
        <div style={{ padding: '20px 8px' }}>
          <Spinner size={SpinnerSize.medium} label="Loading metrics…" ariaLive="polite" labelPosition="right" />
        </div>
      ) : error ? (
        <MessageBar messageBarType={MessageBarType.error} styles={{ root: { flex: 1 } }}>
          {error}
        </MessageBar>
      ) : (
        <>
          {/* ── Estate (now) ── */}
          <Zone label="Estate" tokens={tokens}>
            <KpiTile
              tokens={tokens}
              value={posture.data?.totalManaged ?? 0}
              label="Managed"
              tone="neutral"
              onClick={() => navigate('/applications')}
              ariaLabel={`${posture.data?.totalManaged ?? 0} managed applications. View all applications.`}
            />
            <KpiTile
              tokens={tokens}
              value={posture.data?.expiring30 ?? 0}
              label="Expiring ≤30d"
              tone={(posture.data?.expiring30 ?? 0) > 0 ? 'attention' : 'neutral'}
              onClick={() => navigate('/credentials?expiring=30')}
              ariaLabel={`${posture.data?.expiring30 ?? 0} credentials expiring within 30 days. View expiring credentials.`}
            />
            <KpiTile
              tokens={tokens}
              value={posture.data?.ownership.unowned ?? 0}
              label="Unowned"
              tone={(posture.data?.ownership.unowned ?? 0) > 0 ? 'attention' : 'neutral'}
              onClick={() => navigate('/applications?hasOwner=false')}
              ariaLabel={`${posture.data?.ownership.unowned ?? 0} applications with no owner. View unowned applications.`}
            />
          </Zone>

          <Divider tokens={tokens} />

          {/* ── Activity (window) ── */}
          <Zone
            label={`Activity · last ${days} days`}
            caption={hasPrior ? `vs prior ${days} days` : 'no prior window yet'}
            tokens={tokens}
          >
            <KpiTile
              tokens={tokens}
              value={addedCur}
              label="Added"
              tone="neutral"
              delta={hasPrior ? { value: addedCur - addedPrior, health: false } : undefined}
              ariaLabel={`${addedCur} applications added in the last ${days} days.`}
            />
            <KpiTile
              tokens={tokens}
              value={removedCur}
              label="Removed"
              tone="neutral"
              delta={hasPrior ? { value: removedCur - removedPrior, health: false } : undefined}
              ariaLabel={`${removedCur} applications removed in the last ${days} days.`}
            />
            <KpiTile
              tokens={tokens}
              value={netCur}
              label="Net"
              tone={netCur > 0 ? 'up' : netCur < 0 ? 'down' : 'neutral'}
              signed
              delta={hasPrior ? { value: netCur - netPrior, health: true } : undefined}
              ariaLabel={`Net change of ${netCur > 0 ? 'plus ' : ''}${netCur} applications in the last ${days} days.`}
            />
          </Zone>

          <div style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
            <SyncChip sync={sync.data} tokens={tokens} onClick={() => navigate('/settings')} />
          </div>
        </>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function Zone({
  label,
  caption,
  tokens,
  children,
}: {
  label: string
  caption?: string
  tokens: DashboardTokens
  children: ReactNode
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: tokens.muted,
          }}
        >
          {label}
        </span>
        {caption ? <span style={{ fontSize: 11, color: tokens.muted }}>· {caption}</span> : null}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{children}</div>
    </div>
  )
}

function Divider({ tokens }: { tokens: DashboardTokens }) {
  return <div aria-hidden style={{ width: 1, alignSelf: 'stretch', background: tokens.line, margin: '4px 0' }} />
}

type Tone = 'neutral' | 'attention' | 'up' | 'down'

interface KpiTileProps {
  tokens: DashboardTokens
  value: number
  label: string
  tone: Tone
  /** Show an explicit + sign for positive values (used by Net). */
  signed?: boolean
  delta?: { value: number; health: boolean }
  onClick?: () => void
  ariaLabel?: string
}

function KpiTile({ tokens, value, label, tone, signed, delta, onClick, ariaLabel }: KpiTileProps) {
  const valueColor =
    tone === 'attention'
      ? tokens.amber
      : tone === 'up'
        ? tokens.up
        : tone === 'down'
          ? tokens.down
          : tokens.text

  const display = signed && value > 0 ? `+${value}` : String(value)

  const tileStyle: CSSProperties = {
    minWidth: 96,
    padding: '10px 14px',
    borderRadius: 10,
    background: tokens.card2 || tokens.elevated,
    border: `1px solid ${tokens.line}`,
    textAlign: 'left',
    cursor: onClick ? 'pointer' : 'default',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  }

  const inner = (
    <>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1, color: valueColor }}>{display}</span>
        {delta ? <DeltaPill tokens={tokens} value={delta.value} health={delta.health} /> : null}
      </span>
      <span style={{ fontSize: 12, color: tokens.slate }}>{label}</span>
    </>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} aria-label={ariaLabel} style={tileStyle}>
        {inner}
      </button>
    )
  }
  return (
    <div role="group" aria-label={ariaLabel} style={tileStyle}>
      {inner}
    </div>
  )
}

/**
 * Delta chip. The arrow glyph is the non-colour cue (1.4.1). `health=false`
 * (raw Added/Removed) renders neutral; `health=true` (Net) colours up=good /
 * down=bad. A zero delta reads "no change".
 */
function DeltaPill({ tokens, value, health }: { tokens: DashboardTokens; value: number; health: boolean }) {
  const dir = value > 0 ? 'up' : value < 0 ? 'down' : 'flat'
  const glyph = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '—'
  const color = !health || dir === 'flat' ? tokens.muted : dir === 'up' ? tokens.up : tokens.down
  const bg = !health || dir === 'flat' ? 'transparent' : dir === 'up' ? tokens.upBg : tokens.downBg

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 12,
        fontWeight: 600,
        color,
        background: bg,
        padding: bg === 'transparent' ? 0 : '1px 6px',
        borderRadius: 10,
      }}
    >
      <span aria-hidden>{glyph}</span>
      {Math.abs(value)}
    </span>
  )
}

function SyncChip({
  sync,
  tokens,
  onClick,
}: {
  sync: SyncHealth | null
  tokens: DashboardTokens
  onClick: () => void
}) {
  if (!sync) return null

  const stale = sync.stale
  const color = stale ? tokens.amberOn : tokens.addedOn
  const bg = stale ? tokens.amberSoft : tokens.addedSoft
  const label = describeSync(sync)

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label}. ${stale ? 'Sync is stale. ' : ''}Open sync settings.`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 12px',
        borderRadius: 999,
        border: `1px solid ${tokens.line}`,
        background: bg,
        color,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      <Icon iconName={stale ? 'Warning' : 'SkypeCircleCheck'} style={{ fontSize: 13 }} aria-hidden />
      {label}
    </button>
  )
}

function describeSync(sync: SyncHealth): string {
  if (sync.lastSyncAt == null || sync.hoursSinceSync == null) return 'Never synced'
  const h = sync.hoursSinceSync
  if (h < 1) return 'Synced <1h ago'
  if (h < 24) return `Synced ${Math.round(h)}h ago`
  const d = Math.floor(h / 24)
  return `Last sync ${d}d ago`
}

export default KpiBand
